import { useState, useEffect } from "react";
import { DropboxStorageAdapter } from "../data/storageAdapters/DropboxStorageAdapter";

const DROPBOX_APP_KEY = import.meta.env.VITE_DROPBOX_APP_KEY;
const DROPBOX_APP_SECRET = import.meta.env.VITE_DROPBOX_APP_SECRET;
const DROPBOX_TOKEN_KEY = "excalidraw-dropbox-token";

export const useDropboxBackup = () => {
  const [adapter, setAdapter] = useState<DropboxStorageAdapter | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!DROPBOX_APP_KEY || !DROPBOX_APP_SECRET) {
      console.warn(
        "Dropbox credentials not configured. Please set VITE_DROPBOX_APP_KEY and VITE_DROPBOX_APP_SECRET environment variables.",
      );
      setIsLoading(false);
      return;
    }

    const storedToken = localStorage.getItem(DROPBOX_TOKEN_KEY);
    const newAdapter = new DropboxStorageAdapter({
      appKey: DROPBOX_APP_KEY,
      appSecret: DROPBOX_APP_SECRET,
      accessToken: storedToken || undefined,
    });

    if (storedToken) {
      newAdapter.setAccessToken(storedToken);
      setIsAuthenticated(true);
    }

    setAdapter(newAdapter);
    setIsLoading(false);

    const handleMessage = async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === "dropbox-auth" && event.data?.code) {
        setIsLoading(true);
        try {
          const redirectUri = window.location.origin;
          await newAdapter.exchangeCodeForToken(event.data.code, redirectUri);
          
          const token = (newAdapter as any).accessToken;
          if (token) {
            localStorage.setItem(DROPBOX_TOKEN_KEY, token);
            setIsAuthenticated(true);
          }
        } catch (error) {
          console.error("Failed to authenticate with Dropbox:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const startAuth = async () => {
    if (!adapter) {
      return;
    }

    const redirectUri = window.location.origin;
    const authUrl = await adapter.authenticate(redirectUri);
    
    const width = 600;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    window.open(
      authUrl,
      "Dropbox Authorization",
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  const disconnect = () => {
    localStorage.removeItem(DROPBOX_TOKEN_KEY);
    setIsAuthenticated(false);
    setAdapter(new DropboxStorageAdapter({
      appKey: DROPBOX_APP_KEY,
      appSecret: DROPBOX_APP_SECRET,
    }));
  };

  return {
    adapter,
    isAuthenticated,
    isLoading,
    startAuth,
    disconnect,
  };
};
